import React from 'react';
import { TCN } from './tokens';

/**
 * TCN CTAButton — primary (cyan) or secondary (outline).
 * Full-width by default. Leading/trailing icon slots.
 *
 * Props:
 *  - variant: 'primary' | 'secondary' | 'ghost' | 'danger' (default 'primary')
 *  - size: 'sm' | 'md' | 'lg' (default 'md')
 *  - fullWidth: boolean (default true)
 *  - leading, trailing: ReactNode icon slots
 *  - loading: boolean
 *  - disabled, onClick, type, children
 */
// Mockup CTA primary spec (ui-kit.jsx:449-464):
//   padding 14×16, borderRadius 14, fontSize 15, fontWeight 700, letterSpacing -0.1
//   bg = TCN.text (#f5f5f7) for primary, surface2 for secondary
//   NO box-shadow / glow
const SIZE = {
  sm: { padY: 10, padX: 14, fs: 14, h: 40 },
  md: { padY: 14, padX: 16, fs: 15, h: 48 }, // mockup default
  lg: { padY: 16, padX: 20, fs: 16, h: 56 },
};

const CTAButton = ({
  variant = 'primary',
  size = 'md',
  fullWidth = true,
  leading,
  trailing,
  loading = false,
  disabled = false,
  type = 'button',
  onClick,
  className = '',
  style,
  children,
  ...rest
}) => {
  const sz = SIZE[size] ?? SIZE.md;

  // Aligned on mockup ui-kit.jsx:449-464 :
  //   primary = bg TCN.text (white-cream) / fg #000 / no glow
  //   secondary = bg surface2 fillé / fg text / border (0.08)
  // ghost + danger sont des extensions code (pas dans le mockup) — gardées.
  const variants = {
    primary: {
      background: TCN.text,
      color: '#000',
      border: 'none',
    },
    secondary: {
      background: TCN.surface2,
      color: TCN.text,
      border: `1px solid ${TCN.border}`,
    },
    ghost: {
      background: 'transparent',
      color: TCN.cyan,
      border: '1px solid transparent',
    },
    danger: {
      background: TCN.magenta,
      color: '#fff',
      border: '1px solid transparent',
    },
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center transition-all active:scale-[0.98] ${className}`}
      style={{
        width: fullWidth ? '100%' : 'auto',
        height: sz.h,
        padding: `${sz.padY}px ${sz.padX}px`,
        fontSize: sz.fs,
        fontWeight: 700,             // mockup spec — was Tailwind font-semibold (600)
        gap: 8,
        borderRadius: 14,            // mockup spec radius (was TCN.radius.md = 12)
        fontFamily: TCN.fontSans,
        letterSpacing: '-0.1px',     // mockup -0.1 raw px
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled || loading ? 0.6 : 1,
        ...variants[variant],
        ...style,
      }}
      {...rest}
    >
      {loading ? (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          style={{ animation: 'spin 0.8s linear infinite' }}
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
          <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      ) : leading}
      <span>{children}</span>
      {!loading && trailing}
    </button>
  );
};

export default CTAButton;
